import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

function formatRating(r: number | null | undefined): string | null {
  if (r == null || Number.isNaN(r)) return null;
  return Number(r).toFixed(1);
}

export function SellerRatingInline({
  rating,
  count,
  variant = "full",
  className,
}: {
  rating: number | null | undefined;
  count: number | null | undefined;
  variant?: "full" | "compact";
  className?: string;
}) {
  const c = count ?? 0;
  if (c === 0) {
    if (variant === "compact") return null;
    return (
      <span className={cn("text-xs text-muted-foreground", className)}>No reviews yet</span>
    );
  }
  const r = formatRating(rating) ?? "—";
  const label = `Average rating ${r} out of 5 from ${c} review${c === 1 ? "" : "s"}`;
  if (variant === "compact") {
    return (
      <span
        className={cn("inline-flex items-center gap-1 text-xs text-muted-foreground", className)}
        aria-label={label}
      >
        <Star className="h-3 w-3 fill-gold text-gold" aria-hidden />
        <span>{r}</span>
        <span aria-hidden>·</span>
        <span>{c}</span>
      </span>
    );
  }
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-sm text-muted-foreground", className)}
      aria-label={label}
    >
      <Star className="h-3.5 w-3.5 fill-gold text-gold" aria-hidden />
      <span className="text-foreground font-medium">{r}</span>
      <span>
        ({c} review{c === 1 ? "" : "s"})
      </span>
    </span>
  );
}

export function Stars({ rating, className }: { rating: number; className?: string }) {
  const rounded = Math.round(rating);
  return (
    <span
      className={cn("inline-flex items-center gap-0.5", className)}
      role="img"
      aria-label={`Rated ${rating} out of 5`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            "h-3.5 w-3.5",
            i <= rounded ? "fill-gold text-gold" : "text-muted-foreground/40",
          )}
          aria-hidden
        />
      ))}
    </span>
  );
}
